const LOADER_FADE_MS = 180;
const DEFAULT_TITLE = "Loading";
const DEFAULT_HINT = "Please wait…";
const FETCH_HINT = "Please wait while we fetch your data…";

const GENERIC_HINT_STAGES = [
  { atMs: 0, text: DEFAULT_HINT },
  { atMs: 4000, text: "Still working — this can take a moment…" },
  { atMs: 10000, text: "Taking longer than usual. You can hide this." },
];

const FETCH_HINT_STAGES = [
  { atMs: 0, text: FETCH_HINT },
  { atMs: 4000, text: "Still working — this can take a moment…" },
  { atMs: 10000, text: "Taking longer than usual. You can hide this." },
];

const DISMISS_SHOW_MS = 8000;

/**
 * API path → { title, hintStages }.
 * Auth / mutations use neutral "Please wait…"; data fetches use FETCH_HINT.
 */
const API_LOADER_COPY = [
  [
    /userLogout|userlogout|\/logout|sessionStatus/i,
    { title: "Signing out", hintStages: GENERIC_HINT_STAGES },
  ],
  [
    /userLogin|\/login/i,
    { title: "Signing in", hintStages: GENERIC_HINT_STAGES },
  ],
  [
    /forgotPassword|otpVerify|updatePassword|changePassword|firebaseIdUpdate/i,
    { title: "Please wait", hintStages: GENERIC_HINT_STAGES },
  ],
  [
    /depositionReport/i,
    { title: "Loading Disposition Report", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /mdoc/i,
    { title: "Loading MDoc Audit Log", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /\/dashboard/i,
    { title: "Loading Dashboard", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /channelPartner|chanelPartner|getAssignCPList|cpUnderProperty|createCPwithcompany/i,
    { title: "Loading Agency CP", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /\/property/i,
    { title: "Loading Properties", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /\/visit|visitor/i,
    { title: "Loading Visitors", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /followup|followUp/i,
    { title: "Loading Follow-ups", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /webReport|authreport|\/report/i,
    { title: "Loading Reports", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /userManage|getUserList|getUserDetail|userRegister|getUserProfile|editUserProfile/i,
    { title: "Loading User Management", hintStages: FETCH_HINT_STAGES },
  ],
];

const ROUTE_LOADER_COPY = [
  [
    /dispositionReport/i,
    { title: "Loading Disposition Report", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /mdocAuditLog/i,
    { title: "Loading MDoc Audit Log", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /^\/dashboard(?:\/|$)/i,
    { title: "Loading Dashboard", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /channelpartner|channel_partner|add_channel_partner|viewchannelpartner|allocateCpToSM/i,
    { title: "Loading Agency CP", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /property|addnewproperty|editproperty|viewproperty/i,
    { title: "Loading Properties", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /visitor|visitor_form|viewvisitor|verifylead/i,
    { title: "Loading Visitors", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /followup|todayfollowup|appointmentfollowup/i,
    { title: "Loading Follow-ups", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /user(?:\/|$|s)/i,
    { title: "Loading User Management", hintStages: FETCH_HINT_STAGES },
  ],
  [
    /report|rolewiseReport|projectDetailsReport|pdrReport/i,
    { title: "Loading Reports", hintStages: FETCH_HINT_STAGES },
  ],
];

/** @type {Set<symbol>} */
const pendingLoaderRequests = new Set();
let globalLoaderHideTimer = null;
let activeLoaderTitle = DEFAULT_TITLE;
let activeLoaderHint = DEFAULT_HINT;
let hintTimers = [];
let dismissShowTimer = null;
let visuallyDismissed = false;
let dismissBound = false;
/** Bumps on every show/hide/reset so stale rAF callbacks cannot re-open the overlay. */
let loaderGeneration = 0;
let routeListenerBound = false;

function defaultCopy() {
  return { title: DEFAULT_TITLE, hintStages: GENERIC_HINT_STAGES };
}

function getPendingCount() {
  return pendingLoaderRequests.size;
}

export function resolveLoaderCopy(apiUrl = "", explicitMessage = "") {
  if (explicitMessage && String(explicitMessage).trim()) {
    const title = normalizeLoaderTitle(String(explicitMessage).trim());
    const isAuth =
      /sign(?:ing)?\s*out|log(?:ging)?\s*out|sign(?:ing)?\s*in|log(?:ging)?\s*in/i.test(
        title,
      );
    return {
      title,
      hintStages: isAuth ? GENERIC_HINT_STAGES : FETCH_HINT_STAGES,
    };
  }

  const urlPath = String(apiUrl || "").replace(/^\/+/, "");

  for (const [pattern, copy] of API_LOADER_COPY) {
    if (pattern.test(urlPath)) {
      return copy;
    }
  }

  if (typeof window !== "undefined" && window.location?.pathname) {
    const routePath = window.location.pathname.replace(/^\/+/, "");
    for (const [pattern, copy] of ROUTE_LOADER_COPY) {
      if (pattern.test(routePath)) {
        return copy;
      }
    }
  }

  return defaultCopy();
}

/** @deprecated Prefer resolveLoaderCopy — kept for any callers expecting a string. */
export function resolveLoaderMessage(apiUrl = "", explicitMessage = "") {
  return resolveLoaderCopy(apiUrl, explicitMessage).title;
}

function normalizeLoaderTitle(message) {
  if (!message) {
    return DEFAULT_TITLE;
  }
  const trimmed = message.replace(/\s*\.+\s*$/, "").trim();
  if (
    /loading|signing|please wait|verifying|updating|saving/i.test(trimmed)
  ) {
    return trimmed;
  }
  return `Loading ${trimmed}`;
}

function getLoaderElement() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.document.getElementById("loaderoverlay");
}

function getTitleElement() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.document.getElementById("justo-global-loader-message");
}

function getHintElement() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.document.getElementById("justo-global-loader-hint");
}

function getDismissButton() {
  if (typeof window === "undefined") {
    return null;
  }
  return window.document.getElementById("justo-global-loader-dismiss");
}

function clearHintTimers() {
  hintTimers.forEach((id) => window.clearTimeout(id));
  hintTimers = [];
  if (dismissShowTimer) {
    window.clearTimeout(dismissShowTimer);
    dismissShowTimer = null;
  }
}

function setHintText(hint) {
  activeLoaderHint = hint || DEFAULT_HINT;
  const hintEl = getHintElement();
  if (hintEl) {
    hintEl.textContent = activeLoaderHint;
  }
}

function setTitleText(title) {
  activeLoaderTitle = title || DEFAULT_TITLE;
  const titleEl = getTitleElement();
  if (titleEl) {
    titleEl.textContent = activeLoaderTitle;
  }
  const loader = getLoaderElement();
  if (loader) {
    loader.setAttribute(
      "aria-label",
      `${activeLoaderTitle}. ${activeLoaderHint}`,
    );
  }
}

function setDismissVisible(visible) {
  const btn = getDismissButton();
  if (!btn) {
    return;
  }
  if (visible) {
    btn.hidden = false;
    btn.removeAttribute("aria-hidden");
  } else {
    btn.hidden = true;
    btn.setAttribute("aria-hidden", "true");
  }
}

function bindDismissOnce() {
  if (dismissBound || typeof window === "undefined") {
    return;
  }
  const btn = getDismissButton();
  if (!btn) {
    return;
  }
  btn.addEventListener("click", () => {
    // Hide UI only — in-flight requests keep pending slots. Never leave the
    // overlay as display:flex + opacity:0; that still captures all clicks.
    visuallyDismissed = true;
    clearHintTimers();
    setDismissVisible(false);
    loaderGeneration += 1;

    const loader = getLoaderElement();
    if (!loader) {
      return;
    }
    loader.classList.remove("justo-global-loader--visible");
    loader.setAttribute("aria-hidden", "true");
    loader.style.pointerEvents = "none";
    window.setTimeout(() => {
      if (!visuallyDismissed || !loader) {
        return;
      }
      loader.style.display = "none";
      loader.style.pointerEvents = "";
    }, LOADER_FADE_MS);
  });
  dismissBound = true;
}

function startHintStages(hintStages = GENERIC_HINT_STAGES) {
  clearHintTimers();
  const stages =
    Array.isArray(hintStages) && hintStages.length
      ? hintStages
      : GENERIC_HINT_STAGES;
  setHintText(stages[0].text);
  setDismissVisible(false);

  stages.slice(1).forEach((stage) => {
    const id = window.setTimeout(() => {
      if (getPendingCount() > 0 && !visuallyDismissed) {
        setHintText(stage.text);
        const loader = getLoaderElement();
        if (loader) {
          loader.setAttribute(
            "aria-label",
            `${activeLoaderTitle}. ${activeLoaderHint}`,
          );
        }
      }
    }, stage.atMs);
    hintTimers.push(id);
  });

  dismissShowTimer = window.setTimeout(() => {
    if (getPendingCount() > 0 && !visuallyDismissed) {
      setDismissVisible(true);
    }
  }, DISMISS_SHOW_MS);
}

function openLoaderOverlay(generation) {
  const loader = getLoaderElement();
  if (!loader) {
    return;
  }

  if (globalLoaderHideTimer) {
    window.clearTimeout(globalLoaderHideTimer);
    globalLoaderHideTimer = null;
  }

  loader.style.display = "flex";
  loader.style.pointerEvents = "";
  loader.setAttribute("aria-hidden", "false");
  window.requestAnimationFrame(() => {
    // Ignore stale frames from a prior show/hide cycle.
    if (generation !== loaderGeneration || getPendingCount() === 0) {
      return;
    }
    if (visuallyDismissed) {
      return;
    }
    loader.classList.add("justo-global-loader--visible");
  });
}

function closeLoaderOverlay() {
  clearHintTimers();
  visuallyDismissed = false;
  setDismissVisible(false);
  loaderGeneration += 1;

  const loader = getLoaderElement();
  if (!loader) {
    return;
  }

  loader.classList.remove("justo-global-loader--visible");
  loader.setAttribute("aria-hidden", "true");

  if (globalLoaderHideTimer) {
    window.clearTimeout(globalLoaderHideTimer);
  }

  globalLoaderHideTimer = window.setTimeout(() => {
    if (getPendingCount() === 0 && loader) {
      loader.style.display = "none";
      loader.style.pointerEvents = "";
      loader.classList.remove("justo-global-loader--visible");
      setHintText(DEFAULT_HINT);
    }
    globalLoaderHideTimer = null;
  }, LOADER_FADE_MS);
}

/**
 * Clears a stuck overlay when the user navigates to another screen.
 * In-flight requests are abandoned for loader accounting so UI never blocks.
 */
export function bindGlobalLoaderRouteReset() {
  if (routeListenerBound || typeof window === "undefined") {
    return;
  }

  let lastPathname = window.location.pathname;

  const resetOnNavigate = () => {
    const nextPath = window.location.pathname;
    if (nextPath === lastPathname) {
      return;
    }
    lastPathname = nextPath;
    resetGlobalLoader();
  };

  window.addEventListener("popstate", resetOnNavigate);

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    const result = originalPushState.apply(this, args);
    resetOnNavigate();
    return result;
  };

  window.history.replaceState = function replaceState(...args) {
    const result = originalReplaceState.apply(this, args);
    resetOnNavigate();
    return result;
  };

  routeListenerBound = true;
}

export function showGlobalLoader(
  message = DEFAULT_TITLE,
  hintOrStages = GENERIC_HINT_STAGES,
) {
  const requestId = Symbol("global-loader-request");
  pendingLoaderRequests.add(requestId);
  visuallyDismissed = false;
  bindDismissOnce();
  bindGlobalLoaderRouteReset();
  loaderGeneration += 1;
  const generation = loaderGeneration;

  setTitleText(normalizeLoaderTitle(message));

  const hintStages = Array.isArray(hintOrStages)
    ? hintOrStages
    : [
        { atMs: 0, text: hintOrStages || DEFAULT_HINT },
        ...GENERIC_HINT_STAGES.slice(1),
      ];
  startHintStages(hintStages);
  openLoaderOverlay(generation);

  return requestId;
}

export function showGlobalLoaderForRequest(apiUrl, explicitMessage = "") {
  const { title, hintStages } = resolveLoaderCopy(apiUrl, explicitMessage);
  return showGlobalLoader(title, hintStages);
}

export function hideGlobalLoaderIfIdle(requestId) {
  if (requestId && pendingLoaderRequests.has(requestId)) {
    pendingLoaderRequests.delete(requestId);
  } else if (!requestId && getPendingCount() > 0) {
    // Legacy callers without an id: drop one pending slot safely.
    const first = pendingLoaderRequests.values().next().value;
    if (first) {
      pendingLoaderRequests.delete(first);
    }
  }

  if (getPendingCount() !== 0) {
    return;
  }

  closeLoaderOverlay();
}

export function resetGlobalLoader() {
  pendingLoaderRequests.clear();
  activeLoaderTitle = DEFAULT_TITLE;
  activeLoaderHint = DEFAULT_HINT;
  visuallyDismissed = false;
  clearHintTimers();
  loaderGeneration += 1;

  if (globalLoaderHideTimer) {
    window.clearTimeout(globalLoaderHideTimer);
    globalLoaderHideTimer = null;
  }

  const loader = getLoaderElement();
  if (loader) {
    loader.classList.remove("justo-global-loader--visible");
    loader.style.display = "none";
    loader.style.pointerEvents = "";
    loader.setAttribute("aria-hidden", "true");
  }

  setTitleText(DEFAULT_TITLE);
  setHintText(DEFAULT_HINT);
  setDismissVisible(false);
}
